<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { PaginationMeta } from '../../services/api'
import { getProducts } from '../../services/saleApi'
import { ProductInterface } from '../../models/ProductModel'
import ALoader from '../common/ALoader.vue'
import APagination from '../common/APagination.vue'
import AButton from '../common/AButton.vue'
import { SaleInterface } from '../../models/SaleModel'

const props = withDefaults(defineProps<Props>(), {
  selectedProducts: () => [],
  saleData: null,
  initialLoad: false,
})

interface Props {
  selectedProducts?: ProductInterface[]
  saleData?: SaleInterface | null
  initialLoad?: boolean
}

const emit = defineEmits<{
  productSelected: [product: ProductInterface]
}>()

const products = ref<ProductInterface[]>([])
const pagination = ref<PaginationMeta | null>(null)
const isLoading = ref<boolean>(true)
const suppressNextBlurSearch = ref<boolean>(false)

const searchInput = ref<string>('')
const searchQuery = ref<string>('')
const searchEl = ref<HTMLInputElement | null>(null)
const showProducts = ref<boolean>(false)
const disableShowProducts = ref<boolean>(false)

let burstDetectTimer: number | null = null
const enterQueued = ref<boolean>(false)

let searchDebounceTimer: number | null = null

const SEARCH_DEBOUNCE_MS = 350

const debugEnabled = !!(import.meta.env.VITE_DEBUG as number)

const captureEl = ref<HTMLInputElement | null>(null)
const captureValue = ref<string>('')
let commitTimer: number | null = null

const COMMIT_AFTER_MS = 120
const BURST_DETECT_MS = 250
const FOCUS_GUARD_MS = 500

let focusGuardTimer: number | null = null
let latestSearchRequest = 0

const canCaptureFocus = computed(() => {
  return !isLoading.value && document.activeElement !== searchEl.value
})

function selectProduct(product: ProductInterface) {
  const index = props.selectedProducts.findIndex((p) => p.id === product.id)

  if (index === -1) {
    emit('productSelected', product)
    return
  }

  emit('productSelected', props.selectedProducts[index])
}

function isAllowedFocusedElement(el: Element | null): boolean {
  return el === captureEl.value || el === searchEl.value
}

function ensureScannerFocus() {
  if (!canCaptureFocus.value) return

  const active = document.activeElement
  if (isAllowedFocusedElement(active)) return

  void focusCapture()
}

function startFocusGuard() {
  stopFocusGuard()

  focusGuardTimer = window.setInterval(() => {
    ensureScannerFocus()
  }, FOCUS_GUARD_MS)
}

function stopFocusGuard() {
  if (focusGuardTimer) {
    window.clearInterval(focusGuardTimer)
    focusGuardTimer = null
  }
}

async function focusCapture() {
  await nextTick()

  if (!canCaptureFocus.value) return

  const el = captureEl.value
  if (!el) return

  try {
    el.focus({ preventScroll: true })
  } catch {
    el.focus()
  }

  try {
    const len = el.value.length
    el.setSelectionRange(len, len)
  } catch {
    // ignore
  }
}

function scheduleCommit() {
  if (commitTimer) {
    window.clearTimeout(commitTimer)
  }

  commitTimer = window.setTimeout(() => {
    void commitScan()
  }, COMMIT_AFTER_MS)
}

async function runSearch(page: number, search: string, shouldAutoSelect = false): Promise<void> {
  const requestId = ++latestSearchRequest
  isLoading.value = true

  let result: Awaited<ReturnType<typeof getProducts>> | null = null

  try {
    result = await getProducts(page, search)
  } finally {
    isLoading.value = false
  }

  if (requestId !== latestSearchRequest) return
  if (!result) return

  products.value = result.items
  pagination.value = result.pagination
  searchQuery.value = search

  if (shouldAutoSelect && result.items.length === 1) {
    selectProduct(result.items[0])
  }

  requestAnimationFrame(() => {
    const pageTop = document.getElementById('pageTop')
    pageTop?.scrollIntoView({
      block: 'start',
      behavior: 'auto',
    })
  })

  void focusCapture()
}

async function commitScan() {
  const value = (captureValue.value ?? '').trim()
  if (!value) return

  enterQueued.value = true
  searchInput.value = value
  captureValue.value = ''

  await runSearch(1, value, true)
  enterQueued.value = false
}

function decodeKey(e: KeyboardEvent): string | null {
  if (typeof e.key === 'string' && e.key) {
    if (e.key === 'Enter') return 'ENTER'
    if (e.key === 'Tab') return 'TAB'
    if (e.key.length === 1) return e.key
    return null
  }

  const anyE = e as KeyboardEvent & { keyCode?: number; which?: number }
  const code = typeof anyE.keyCode === 'number' ? anyE.keyCode : anyE.which

  if (typeof code !== 'number') return null

  if (code === 13) return 'ENTER'
  if (code === 9) return 'TAB'

  if (code >= 48 && code <= 57) return String.fromCharCode(code)
  if (code >= 96 && code <= 105) return String(code - 96)

  if (code >= 65 && code <= 90) {
    const ch = String.fromCharCode(code)
    return e.shiftKey ? ch : ch.toLowerCase()
  }

  if (code === 189 || code === 109) return e.shiftKey ? '_' : '-'
  if (code === 190 || code === 110) return '.'
  if (code === 191 || code === 111) return '/'

  return null
}

function onCaptureKeydown(e: KeyboardEvent) {
  if (!canCaptureFocus.value) return

  const decoded = decodeKey(e)
  if (!decoded) return

  if (decoded === 'ENTER' || decoded === 'TAB') {
    e.preventDefault()
    e.stopPropagation()
    void commitScan()
    return
  }

  captureValue.value += decoded
  scheduleCommit()
}

function onCaptureBlur() {
  window.setTimeout(() => {
    if (!canCaptureFocus.value) return

    const active = document.activeElement
    if (active === searchEl.value) return
    if (active === captureEl.value) return

    void focusCapture()
  }, 0)
}

async function onSearchKeyUp(e: KeyboardEvent) {
  showProducts.value = !!searchInput.value

  if (e.key !== 'Enter') return

  if (searchDebounceTimer) {
    window.clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }

  enterQueued.value = true
  suppressNextBlurSearch.value = true
  await runSearch(1, searchInput.value.trim(), true)
  enterQueued.value = false
}

async function onSearchFocus() {
  disableShowProducts.value = true
}

async function onSearchBlur() {
  setTimeout(() => {
    disableShowProducts.value = false
  }, 50)

  if (suppressNextBlurSearch.value) {
    suppressNextBlurSearch.value = false
  }

  window.setTimeout(() => {
    void focusCapture()
  }, 0)
}

function changePage(page: number): void {
  void runSearch(page, searchQuery.value, false)
}

function onSearch(event) {
  if (!event.target.value) {
    showProducts.value = false
  }
}

watch(searchInput, (value, oldValue) => {
  if (burstDetectTimer) {
    window.clearTimeout(burstDetectTimer)
  }

  burstDetectTimer = window.setTimeout(() => {
    const grewByMoreThanOne = value.length - oldValue.length > 1

    if (!grewByMoreThanOne) return

    enterQueued.value = true
    void runSearch(1, value.trim(), true).finally(() => {
      enterQueued.value = false
    })
  }, BURST_DETECT_MS)
})

watch(
  () => canCaptureFocus.value,
  (canFocus) => {
    if (canFocus) {
      void focusCapture()
      startFocusGuard()
      return
    }

    stopFocusGuard()
  },
  { immediate: true }
)

watch(searchInput, (value) => {
  showProducts.value = !!value

  if (searchDebounceTimer) {
    window.clearTimeout(searchDebounceTimer)
  }

  searchDebounceTimer = window.setTimeout(() => {
    void runSearch(1, value.trim(), false)
  }, SEARCH_DEBOUNCE_MS)
})

onMounted(() => {
  void runSearch(1, '', false)

  if (canCaptureFocus.value) {
    void focusCapture()
  }
})

onBeforeUnmount(() => {
  if (burstDetectTimer) {
    window.clearTimeout(burstDetectTimer)
  }

  if (commitTimer) {
    window.clearTimeout(commitTimer)
  }

  if (searchDebounceTimer) {
    window.clearTimeout(searchDebounceTimer)
  }

  stopFocusGuard()
})
</script>

<template>
  <div class="mb-24">
    <div class="mb-12">{{ $t('choose-product') }}:</div>

    <input
      ref="captureEl"
      v-model="captureValue"
      class="form-control search"
      :class="{ 'scanner-capture': !debugEnabled }"
      type="text"
      autocomplete="off"
      spellcheck="false"
      inputmode="none"
      enterkeyhint="done"
      aria-hidden="true"
      tabindex="-1"
      @keydown="onCaptureKeydown"
      @blur="onCaptureBlur"
    />

    <input
      ref="searchEl"
      v-model="searchInput"
      type="search"
      class="form-control search mb-12"
      :placeholder="$t('search')"
      autocomplete="off"
      spellcheck="false"
      @keyup="onSearchKeyUp"
      @focus="onSearchFocus"
      @blur="onSearchBlur"
      @search="onSearch"
    />

    <ALoader v-if="isLoading" />

    <template v-else>
      <AButton
        class="w-100"
        :disabled="disableShowProducts"
        @click="
          () => {
            showProducts = !showProducts
          }
        "
      >
        {{ $t(showProducts ? 'hide-products' : 'show-products') }}
      </AButton>
      <div v-if="showProducts" class="mt-12">
        <div
          v-for="(product, productIndex) in products"
          :key="product.id ?? productIndex"
          class="customer-item"
        >
          <button type="button" @click="selectProduct(product)">
            <b>{{ product.name }}</b>
            <small
              >{{ $t('code') }}:&nbsp;{{ product.code }} | {{ $t('barcode') }}:&nbsp;{{
                product.barcode
              }}</small
            >
          </button>
        </div>
        <div v-if="!products.length">
          {{ $t('products-not-found') }}
        </div>
        <APagination
          v-if="pagination"
          :pagination="pagination"
          class="mt-24"
          @page-change="changePage"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.scanner-capture {
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
</style>
